import React from 'react';
import { Alert, Box, Breadcrumbs, Button, Link, Typography } from '@mui/material';
import { ArrowLeft, Search, Layers, Building2, X } from 'lucide-react';
import { useIntl } from 'react-intl';
import PortalSearch from './portal-search';
import { portalColors, portalFocus, portalLogo } from '../portal-styles';

export default function PortalCategoryHeader({ category, organizationName, trail, fallbackParentName, image,
  onBack, onPortal, onNavigate, searchOptions, loading, error, onRetry }) {
  const intl = useIntl();
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [imageFailed, setImageFailed] = React.useState(false);
  const showImage = Boolean(image && !imageFailed);
  React.useEffect(() => setImageFailed(false), [image]);
  const navigationButton = { ...portalFocus, minHeight: 44, textTransform: 'none', fontWeight: 400, color: portalColors.muted, borderRadius: '999px', px: 1.5 };

  return <Box sx={{ width: '100%', alignSelf: 'stretch', mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
      <Button color="inherit" startIcon={<ArrowLeft size={17} strokeWidth={1.6} aria-hidden="true" />} onClick={onBack} sx={navigationButton}>
        {intl.formatMessage({ id: 'back' })}
      </Button>
      <Button color="inherit" startIcon={searchOpen ? <X size={17} aria-hidden="true" /> : <Search size={17} strokeWidth={1.6} aria-hidden="true" />}
        aria-expanded={searchOpen} aria-controls={searchOpen ? 'portal-category-catalog-search' : undefined}
        onClick={() => setSearchOpen(open => !open)} sx={{ ...navigationButton, bgcolor: portalColors.soft, color: portalColors.action }}>
        {intl.formatMessage({ id: 'portal.catalog.search' })}
      </Button>
    </Box>
    {searchOpen && <Box id="portal-category-catalog-search" sx={{ mb: 3, p: { xs: 2, sm: 3 }, bgcolor: portalColors.soft, borderRadius: '20px' }}>
      <PortalSearch options={searchOptions} loading={loading} autoFocus id="portal-category-global-search"
        label={intl.formatMessage({ id: 'portal.catalog.search.label' })}
        onSelect={item => { setSearchOpen(false); onNavigate(item); }} />
    </Box>}
    {error && <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={onRetry}>
      {intl.formatMessage({ id: 'portal.retry' })}
    </Button>}>{intl.formatMessage({ id: 'portal.load.error' })}</Alert>}
    <Breadcrumbs aria-label={intl.formatMessage({ id: 'portal.breadcrumb' })} sx={{ mb: 3,
      '& .MuiBreadcrumbs-ol': { rowGap: 1 }, '& .MuiTypography-root': { fontSize: 13, color: portalColors.muted, overflowWrap: 'anywhere' },
      '& button': { ...portalFocus, minHeight: 32 },
      '& .MuiLink-root': { color: `${portalColors.muted} !important`, '&:hover': { textDecoration: 'underline !important' } },
    }}>
      <Link component="button" type="button" underline="hover" onClick={onPortal}>{intl.formatMessage({ id: 'portal' })}</Link>
      {organizationName && <Link component="button" type="button" underline="hover" onClick={onPortal}>{organizationName}</Link>}
      {fallbackParentName && fallbackParentName !== category.name && <Typography>{fallbackParentName}</Typography>}
      {trail.map((item, index) => index === trail.length - 1
        ? <Typography key={item.id} aria-current="page">{item.name}</Typography>
        : <Link key={item.id} component="button" type="button" underline="hover" onClick={() => onNavigate(item)}>{item.name}</Link>)}
    </Breadcrumbs>
    <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'flex-start', pb: 4, borderBottom: `1px solid ${portalColors.line}` }}>
      <Box sx={{ width: showImage ? { xs: 96, sm: 128 } : { xs: 48, sm: 64 }, height: showImage ? { xs: 64, sm: 80 } : { xs: 48, sm: 64 },
        p: showImage ? 0.5 : 0, boxSizing: 'border-box', borderRadius: '12px',
        bgcolor: showImage ? portalColors.paper : portalColors.light, color: portalColors.action,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
        {showImage
          ? <Box component="img" src={image} alt="" onError={() => setImageFailed(true)} sx={portalLogo} />
          : <Layers size={27} strokeWidth={1.4} aria-hidden="true" />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 27, sm: 36 }, fontWeight: 500, color: portalColors.ink,
          lineHeight: 1.25, letterSpacing: '-0.03em', overflowWrap: 'anywhere' }}>{category.name}</Typography>
        {organizationName && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5, color: portalColors.muted }}>
          <Building2 size={16} strokeWidth={1.6} style={{ flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{organizationName}</Typography>
        </Box>}
        {category.description && <Typography component="div" sx={{ mt: 2, maxWidth: '70ch', color: portalColors.muted, fontSize: 16, lineHeight: 1.65,
          overflowWrap: 'anywhere', '& p': { my: 0.5 }, '& img': { maxWidth: '100%', height: 'auto' } }}
          dangerouslySetInnerHTML={{ __html: category.description }} />}
      </Box>
    </Box>
  </Box>;
}
